import moment from "moment";
import _isEmpty from "lodash/isEmpty";
import * as timestamp_pb from "google-protobuf/google/protobuf/timestamp_pb";

import { INGESTER_CLIENT, getRequestMetadata } from "../index";
import { convertTimestamp, getDateFromString } from "../../util";

const { GetAnalyticsTransactionsRequest } = require("../../protos/ingester_pb");

const getAnalyticsTransactions = ({
  organizationId,
  startDate,
  endDate,
  userId,
  projectId,
  limit,
  offset,
  requestIds,
  export_records = false
}) => {
  const request = new GetAnalyticsTransactionsRequest();
  const startTs = new timestamp_pb.Timestamp();
  const s = getDateFromString(startDate);
  startTs.setSeconds(s?.seconds);
  startTs.setNanos(0);
  request.setStartTime(startTs);

  !_isEmpty(organizationId) && request.setOrganizationId(organizationId);

  const endTs = new timestamp_pb.Timestamp();
  const e = getDateFromString(endDate);
  endTs.setSeconds(e?.seconds);
  endTs.setNanos(0);
  request.setEndTime(endTs);
  request.setExportRequested(export_records);

  request.setProjectId(projectId);
  request.setUserId(userId);

  request.setLimit(limit || 15);
  request.setOffset(offset);

  if (requestIds?.length) {
    request.setRequestIdsList(requestIds);
  }

  return new Promise((resolve, reject) => {
    INGESTER_CLIENT.getAnalyticsTransactions(request, getRequestMetadata(), (err, response) => {
      if (err) {
        console.warn(".getAnalyticsTransactions", err.code, err.message);
        reject(err);
      } else {
        const nextOffset = response.u[1];
        const transactions = response.u[0].map((transactionProto) => convertTransactionFromProto(transactionProto));
        resolve({ transactions, nextOffset });
      }
    });
  });
};

export const getAnalyticsTransactionsForDownload = async ({ startDate, endDate, userId, projectId, requestIds }) => {
  let offset = 0;
  let transactionsList = [];

  do {
    const response = await getAnalyticsTransactions({
      startDate,
      endDate,
      userId,
      projectId,
      requestIds,
      limit: 1000,
      offset,
      export_records: true
    });
    const { transactions, nextOffset } = response;
    transactionsList = [...transactionsList, ...transactions];
    offset = nextOffset;
  } while (offset != -1);

  // Initialize an array to hold all rows for CSV
  let rows = [];

  transactionsList.forEach((row) => {
    // Transaction-level data
    const transactionData = {
      requestUuid: row.requestUuid,
      transactionTime: moment(row.transactionTime).format("MMM DD, YYYY h:mm:ss a"),
      voceraDoctorUserId: row.voceraDoctorUserId,
      inputIntentText: row.inputIntentText,
      inputClientContext: row.inputClientContext,
      inputIntentContext: row.inputIntentContext,
      intentResponse: row.intentResponse,
      intentDetectionMs: row.intentDetectionMs || 0,
      entireTransactionMs: row.entireTransactionMs || 0,
      iusfJson: ""
    };

    // Parse the intentResponse JSON string
    try {
      const intentResponse = JSON.parse(row.intentResponse);

      // Get iusf_json and include it in the transaction data
      transactionData.iusfJson = intentResponse.iusf_json || "";

      if (intentResponse.intent_result) {
        const intentResult = intentResponse.intent_result;

        // Get intentType
        const intentType = intentResult.intent?.type || "";

        // Get slots
        const slots = intentResult.slots || [];

        // Initialize an object to hold slot columns
        const slotColumns = {};

        slots.forEach((slot, index) => {
          // Get the first slot_value_accuracy from slot_values
          const slotValues = slot.slot_values || [];
          let slotValueAccuracy = "";

          if (slotValues.length > 0) {
            slotValueAccuracy = slotValues[0].slot_value_accuracy || "";
          }

          // Create column names as "Slot Type 1", "Slot Type 2", etc.
          const columnName = `Slot Type ${index + 1}`;
          slotColumns[columnName] = slotValueAccuracy;
        });

        // Create a row combining transaction data, intentType, and slotColumns
        rows.push({
          ...transactionData,
          intentType,
          ...slotColumns
        });
      } else {
        // If intent_result is missing, create a row with empty intentType
        rows.push({
          ...transactionData,
          intentType: ""
        });
      }
    } catch (error) {
      console.error("Error parsing intentResponse:", error);
      // In case of parsing error, create a row with empty intentType
      rows.push({
        ...transactionData,
        intentType: ""
      });
    }
  });

  return rows;
};

const convertTransactionFromProto = (transactionProto) => {
  return {
    requestUuid: transactionProto[0],
    intentDetectionMs: transactionProto[1],
    entireTransactionMs: transactionProto[2],
    voceraSessionId: transactionProto[3],
    voceraDoctorUserId: transactionProto[4],
    voceraProjectId: transactionProto[5],
    voceraFacilityId: transactionProto[6],
    inputClientContext: transactionProto[7],
    inputIntentContext: transactionProto[8],
    inputIntentText: transactionProto[9],
    transactionTime: convertTimestamp(transactionProto[10]),
    audiofilePath: transactionProto[11],
    audioDurationMs: transactionProto[12],
    audioBytes: transactionProto[13],
    intentResponse: transactionProto[14],
    internal: transactionProto[15]
  };
};

export { convertTransactionFromProto, getAnalyticsTransactions };
